// API-helpere til statistiksiden (fase 3). Kontrakten er defineret af backend-arbejdet,
// som køres parallelt - se opgavebeskrivelsen. Endepunkterne behøver ikke eksistere
// for at frontend kan bygges.
import { api } from './api'
import type { BidType, TrumpSuit } from '../../../shared/types'

export interface ClubStatsStanding {
  userId: number
  name: string
  points: number
  games: number
  wins: number
}

export interface ClubStatsBid {
  bidType: BidType
  count: number
  won: number
  winRate: number
}

export interface ClubStatsPartner {
  a: number
  b: number
  aName: string
  bName: string
  rounds: number
  won: number
  points: number
}

export interface ClubStatsBidderPartner {
  bidderId: number
  partnerId: number
  count: number
  won: number
}

export interface ClubStatsTrump {
  suit: TrumpSuit
  count: number
  won: number
  winRate: number
}

export interface ClubStatsPlayerCounter {
  count: number
  won: number
}

export interface ClubStatsPlayer {
  userId: number
  name: string
  asBidder: ClubStatsPlayerCounter
  asPartner: ClubStatsPlayerCounter
  solos: ClubStatsPlayerCounter
  favoriteBid: BidType | null
  favoriteTrump: TrumpSuit | null
}

export interface BiggestRoundRecord {
  gameId: number
  gameName: string
  roundNumber: number
  bidderName: string
  bidType: BidType
  score: number
}

export interface LongestNightRecord {
  gameId: number
  gameName: string
  rounds: number
}

export interface BestNightRecord {
  gameId: number
  gameName: string
  userId: number
  name: string
  points: number
}

export interface MostSolosRecord {
  userId: number
  name: string
  count: number
}

export interface ClubStatsRecords {
  biggestRound: BiggestRoundRecord | null
  longestNight: LongestNightRecord | null
  bestNight: BestNightRecord | null
  mostSolos: MostSolosRecord | null
}

export interface ClubStats {
  year: number
  gamesCount: number
  roundsCount: number
  standing: ClubStatsStanding[]
  bids: ClubStatsBid[]
  partners: ClubStatsPartner[]
  bidderPartners: ClubStatsBidderPartner[]
  trumps: ClubStatsTrump[]
  players: ClubStatsPlayer[]
  records: ClubStatsRecords
}

export interface MyStatsCounter {
  count: number
  won: number
  winRate: number
}

export interface MyStatsBestPartner {
  userId: number
  name: string
  rounds: number
  points: number
}

export interface MyStatsBestNight {
  gameId: number
  gameName: string
  points: number
}

export interface MyStats {
  games: number
  rounds: number
  totalPoints: number
  asBidder: MyStatsCounter
  asPartner: MyStatsCounter
  asOpponent: MyStatsCounter
  solos: { count: number; won: number }
  favoriteBid: BidType | null
  favoriteTrump: TrumpSuit | null
  bestPartner: MyStatsBestPartner | null
  bestNight: MyStatsBestNight | null
}

export async function getClubStats(clubId: number, year: number): Promise<ClubStats> {
  const response = await api.get<ClubStats>(`/clubs/${clubId}/stats`, { params: { year } })
  return response.data
}

export async function getMyStats(): Promise<MyStats> {
  const response = await api.get<MyStats>('/users/me/stats')
  return response.data
}

// --- Spildetaljer til aftenens pointkurve (samme form som GameApi i pages/Game.tsx) ---

export interface StatsGamePlayer {
  userId: number
  name: string
  position: number
  totalScore: number
}

export interface StatsGameRound {
  roundNumber: number
  bidderId: number
  partnerId?: number
  bidType: BidType
  trumpSuit?: TrumpSuit
  tricksTaken: number
  won?: boolean
  score: number
}

export interface StatsGameDetail {
  id: number
  name: string
  status: 'active' | 'completed'
  players: StatsGamePlayer[]
  rounds: StatsGameRound[]
}

export async function getGameDetail(gameId: number): Promise<StatsGameDetail> {
  const response = await api.get<StatsGameDetail>(`/games/${gameId}`)
  return response.data
}
